import {
  Building2,
  Calendar,
  CheckSquare,
  Eye,
  FileText,
  Handshake,
  MessageCircle,
  MessagesSquare,
  MoreHorizontal,
  Plus,
  Search,
  SlidersHorizontal,
  Users,
  Video,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { Input } from "../../../../../components/ui/input";
import { Switch } from "../../../../../components/ui/switch";
import { VECTOR_OBJECTS } from "../../../../../components/ai-memory/constants";
import {
  queryClient,
  useAIMemoryObjects,
  useEnableAIMemoryObject,
  useWorkspaceObjectRelationships,
  useUpdateWorkspaceObject,
  useWorkspaceObjectsDetailed,
} from "../../../../../hooks/QueryHooks";
import { cn } from "../../../../../lib/utils";
import type { AIMemoryObjectType } from "../../../../../services/aiMemory/aiMemoryEndpoints.interface";
import type { WorkspaceObjectRelationshipSummary } from "../../../../../services/workspace/workspaceEndpoints.interface";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { toast } from "sonner";
import type { ObjectWithIntegrations } from "../types";
import ObjectDetailView from "./ObjectDetailView";

type AITraitState = {
  status?: string;
  field_count?: number;
  object_id?: string;
};

type WorkspaceObjectRow = ObjectWithIntegrations & {
  record_count?: number | null;
  records_count?: number | null;
};

type ObjectsTableRow = WorkspaceObjectRow & {
  isVectorObject: boolean;
  aiMemoryStatus?: AITraitState;
};

const ICON_STYLE_BY_SLUG: Record<string, { icon: React.ElementType; color: string }> = {
  accounts: { icon: Building2, color: "bg-blue-500" },
  companies: { icon: Building2, color: "bg-blue-500" },
  users: { icon: Users, color: "bg-green-500" },
  contacts: { icon: Users, color: "bg-green-500" },
  people: { icon: Users, color: "bg-emerald-500" },
  deals: { icon: Handshake, color: "bg-orange-500" },
  meetings: { icon: Calendar, color: "bg-purple-500" },
  tasks: { icon: CheckSquare, color: "bg-muted-foreground" },
  notes: { icon: FileText, color: "bg-blue-500" },
  conversation_messages: { icon: MessageCircle, color: "bg-green-500" },
  conversations: { icon: MessagesSquare, color: "bg-cyan-500" },
  meeting_recordings: { icon: Video, color: "bg-violet-500" },
};

const defaultIconStyle = { icon: Building2, color: "bg-muted-foreground" };

const normalizeAiMemoryMap = (rows: Array<any> | undefined): Record<string, AITraitState> => {
  const map: Record<string, AITraitState> = {};
  (rows || []).forEach((row) => {
    if (!row?.api_slug) return;
    map[row.api_slug] = {
      status: row.status,
      field_count: row.field_count,
      object_id: row.object_id,
    };
  });
  return map;
};

const isProtectedObject = (slug: string) => slug === "accounts" || slug === "users";

// ─── Relationship Graph (unchanged) ──────────────────────────────────

type RelationshipEdge = {
  id: string;
  sourceSlug: string;
  targetSlug: string;
  label: string;
  cardinality: string;
  isEnabled: boolean;
  isSystem: boolean;
  curveOffset: number;
};

type RelationshipNode = {
  slug: string;
  label: string;
  enabled: boolean;
  isSystemObject: boolean;
  fieldCount: number;
  accentColor: string;
};

const PREVIEW_NODE_LIMIT = 8;
const GRAPH_NODE_WIDTH = 180;
const GRAPH_NODE_HEIGHT = 66;

const getNodeAccentColor = (slug: string): string => {
  switch (slug) {
    case "accounts":
    case "companies":
      return "#3b82f6";
    case "users":
    case "contacts":
    case "people":
      return "#22c55e";
    case "deals":
      return "#f97316";
    case "meetings":
      return "#a855f7";
    case "tasks":
      return "#64748b";
    case "notes":
      return "#0ea5e9";
    case "conversation_messages":
      return "#16a34a";
    case "conversations":
      return "#06b6d4";
    case "meeting_recordings":
      return "#7c3aed";
    default:
      return "#64748b";
  }
};

const normalizeCardinality = (cardinality?: string | null): string => {
  const normalized = String(cardinality || "").trim().toLowerCase();
  if (normalized === "one_to_one") return "one_to_one";
  if (normalized === "one_to_many") return "one_to_many";
  if (normalized === "many_to_one") return "many_to_one";
  return "many_to_many";
};

const getCardinalityLabel = (cardinality?: string | null): string => {
  const normalized = normalizeCardinality(cardinality);
  if (normalized === "one_to_one") return "1:1";
  if (normalized === "one_to_many") return "1:N";
  if (normalized === "many_to_one") return "N:1";
  return "M:N";
};

const buildRelationshipGraphData = (
  objects: ObjectsTableRow[],
  relationships: WorkspaceObjectRelationshipSummary[],
) => {
  const objectBySlug = new Map<string, RelationshipNode>();

  objects.forEach((object) => {
    objectBySlug.set(object.api_slug, {
      slug: object.api_slug,
      label: object.plural_noun || object.api_slug,
      enabled: Boolean(object.enabled),
      isSystemObject: Boolean(object.is_system_object),
      fieldCount: object.field_count || object.aiMemoryStatus?.field_count || 0,
      accentColor: getNodeAccentColor(object.api_slug),
    });
  });

  const edgeMap = new Map<string, Omit<RelationshipEdge, "curveOffset">>();

  relationships.forEach((relationship) => {
    const sourceSlug = String(relationship?.source_object_slug || "").trim().toLowerCase();
    const targetSlug = String(relationship?.destination_object_slug || "").trim().toLowerCase();
    if (!sourceSlug || !targetSlug || sourceSlug === targetSlug) return;

    if (!objectBySlug.has(sourceSlug)) {
      objectBySlug.set(sourceSlug, {
        slug: sourceSlug,
        label: relationship.source_object_plural_noun || sourceSlug,
        enabled: relationship.source_object_enabled !== false,
        isSystemObject: Boolean(relationship.source_object_is_system_object),
        fieldCount: 0,
        accentColor: getNodeAccentColor(sourceSlug),
      });
    }

    if (!objectBySlug.has(targetSlug)) {
      objectBySlug.set(targetSlug, {
        slug: targetSlug,
        label: relationship.destination_object_plural_noun || targetSlug,
        enabled: relationship.destination_object_enabled !== false,
        isSystemObject: Boolean(relationship.destination_object_is_system_object),
        fieldCount: 0,
        accentColor: getNodeAccentColor(targetSlug),
      });
    }

    const [left, right] = [sourceSlug, targetSlug].sort();
    const normalizedLabel = String(relationship.relationship_label || "associated_with").trim().toLowerCase();
    const edgeKey = `${left}::${right}::${normalizedLabel}`;

    const existing = edgeMap.get(edgeKey);
    if (existing) {
      existing.isEnabled = existing.isEnabled || Boolean(relationship.is_enabled);
      if (existing.cardinality === "many_to_many" && relationship.cardinality) {
        existing.cardinality = normalizeCardinality(relationship.cardinality);
      }
      return;
    }

    edgeMap.set(edgeKey, {
      id: edgeKey,
      sourceSlug: left,
      targetSlug: right,
      label: normalizedLabel,
      cardinality: normalizeCardinality(relationship.cardinality),
      isEnabled: Boolean(relationship.is_enabled),
      isSystem: Boolean(relationship.is_system),
    });
  });

  const edgesWithoutCurves = Array.from(edgeMap.values());
  const pairCounts = new Map<string, number>();
  edgesWithoutCurves.forEach((edge) => {
    const pairKey = `${edge.sourceSlug}::${edge.targetSlug}`;
    pairCounts.set(pairKey, (pairCounts.get(pairKey) || 0) + 1);
  });

  const pairOffsets = new Map<string, number>();
  const edges: RelationshipEdge[] = edgesWithoutCurves.map((edge) => {
    const pairKey = `${edge.sourceSlug}::${edge.targetSlug}`;
    const currentIndex = pairOffsets.get(pairKey) || 0;
    pairOffsets.set(pairKey, currentIndex + 1);

    const totalForPair = pairCounts.get(pairKey) || 1;
    const centeredIndex = currentIndex - (totalForPair - 1) / 2;

    return {
      ...edge,
      curveOffset: centeredIndex * 30,
    };
  });

  const connectedSlugs = new Set<string>();
  edges.forEach((edge) => {
    connectedSlugs.add(edge.sourceSlug);
    connectedSlugs.add(edge.targetSlug);
  });

  const nodes = Array.from(objectBySlug.values())
    .filter((node) => connectedSlugs.has(node.slug))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { nodes, edges };
};

type RelationshipGraphCanvasProps = {
  nodes: RelationshipNode[];
  edges: RelationshipEdge[];
  width: number;
  height: number;
  compact?: boolean;
};

const RelationshipGraphCanvas = ({
  nodes,
  edges,
  width,
  height,
  compact = false,
}: RelationshipGraphCanvasProps) => {
  const positionedNodes = useMemo(() => {
    if (!nodes.length) return [] as Array<RelationshipNode & { x: number; y: number }>;

    const centerX = width / 2;
    const centerY = height / 2;
    const radiusX = Math.max(120, width / 2 - GRAPH_NODE_WIDTH);
    const radiusY = Math.max(100, height / 2 - GRAPH_NODE_HEIGHT);

    return nodes.map((node, index) => {
      const angle = -Math.PI / 2 + (2 * Math.PI * index) / nodes.length;
      return {
        ...node,
        x: centerX + radiusX * Math.cos(angle),
        y: centerY + radiusY * Math.sin(angle),
      };
    });
  }, [height, nodes, width]);

  const positionBySlug = useMemo(
    () => new Map(positionedNodes.map((node) => [node.slug, node])),
    [positionedNodes],
  );

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full">
      <rect width={width} height={height} fill="transparent" />

      {edges.map((edge) => {
        const source = positionBySlug.get(edge.sourceSlug);
        const target = positionBySlug.get(edge.targetSlug);
        if (!source || !target) return null;

        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const normalX = -dy / distance;
        const normalY = dx / distance;

        const controlX = (source.x + target.x) / 2 + normalX * edge.curveOffset;
        const controlY = (source.y + target.y) / 2 + normalY * edge.curveOffset;

        const labelX = 0.25 * source.x + 0.5 * controlX + 0.25 * target.x;
        const labelY = 0.25 * source.y + 0.5 * controlY + 0.25 * target.y;

        const isManyToMany = normalizeCardinality(edge.cardinality) === "many_to_many";
        const cardinalityLabel = getCardinalityLabel(edge.cardinality);
        const strokeColor = edge.isEnabled ? "hsl(var(--muted-foreground))" : "hsl(var(--border))";

        return (
          <g key={edge.id}>
            <path
              d={`M ${source.x} ${source.y} Q ${controlX} ${controlY} ${target.x} ${target.y}`}
              fill="none"
              stroke={strokeColor}
              strokeWidth={edge.isEnabled ? 1.7 : 1.2}
              strokeDasharray={isManyToMany ? "6 4" : edge.isEnabled ? undefined : "4 4"}
              opacity={edge.isEnabled ? 0.85 : 0.45}
            />
            <g transform={`translate(${labelX}, ${labelY})`} opacity={edge.isEnabled ? 0.95 : 0.6}>
              <rect
                x={-16}
                y={-9}
                width={32}
                height={18}
                rx={5}
                fill="hsl(var(--card))"
                stroke="hsl(var(--border))"
              />
              <text
                x={0}
                y={4}
                textAnchor="middle"
                fontSize={10}
                fill="hsl(var(--muted-foreground))"
                style={{ fontWeight: 600 }}
              >
                {cardinalityLabel}
              </text>
            </g>
            <title>{`${edge.sourceSlug} -> ${edge.targetSlug} (${edge.label})`}</title>
          </g>
        );
      })}

      {positionedNodes.map((node) => {
        const nodeX = node.x - GRAPH_NODE_WIDTH / 2;
        const nodeY = node.y - GRAPH_NODE_HEIGHT / 2;
        const labelText = node.label.length > 24 && compact ? `${node.label.slice(0, 21)}...` : node.label;
        return (
          <g key={node.slug} transform={`translate(${nodeX}, ${nodeY})`}>
            <rect
              width={GRAPH_NODE_WIDTH}
              height={GRAPH_NODE_HEIGHT}
              rx={10}
              fill="hsl(var(--card))"
              stroke="hsl(var(--border))"
              opacity={node.enabled ? 1 : 0.72}
            />
            <circle cx={18} cy={18} r={8} fill={node.accentColor} />
            <text
              x={18}
              y={22}
              textAnchor="middle"
              fontSize={9}
              fill="#ffffff"
              style={{ fontWeight: 700, textTransform: "uppercase" }}
            >
              {(node.slug[0] || "?").toUpperCase()}
            </text>
            <text
              x={34}
              y={22}
              fontSize={12}
              fill="hsl(var(--foreground))"
              style={{ fontWeight: 600 }}
            >
              {labelText}
            </text>
            <text x={34} y={40} fontSize={10} fill="hsl(var(--muted-foreground))">
              {node.fieldCount} fields
            </text>
            {!node.enabled && (
              <text x={GRAPH_NODE_WIDTH - 10} y={14} textAnchor="end" fontSize={9} fill="hsl(var(--muted-foreground))">
                disabled
              </text>
            )}
            {node.isSystemObject && (
              <text x={GRAPH_NODE_WIDTH - 10} y={28} textAnchor="end" fontSize={9} fill="hsl(var(--muted-foreground))">
                system
              </text>
            )}
            <title>{`${node.label} (${node.slug})`}</title>
          </g>
        );
      })}
    </svg>
  );
};

type ObjectRelationshipsGraphProps = {
  objects: ObjectsTableRow[];
  relationships: WorkspaceObjectRelationshipSummary[];
  isLoading: boolean;
};

const ObjectRelationshipsGraph = ({
  objects,
  relationships,
  isLoading,
}: ObjectRelationshipsGraphProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const graphData = useMemo(
    () => buildRelationshipGraphData(objects, relationships),
    [objects, relationships],
  );

  const previewNodes = useMemo(() => {
    const nodeDegree = new Map<string, number>();
    graphData.edges.forEach((edge) => {
      nodeDegree.set(edge.sourceSlug, (nodeDegree.get(edge.sourceSlug) || 0) + 1);
      nodeDegree.set(edge.targetSlug, (nodeDegree.get(edge.targetSlug) || 0) + 1);
    });

    return [...graphData.nodes]
      .sort((leftNode, rightNode) => {
        const degreeDelta = (nodeDegree.get(rightNode.slug) || 0) - (nodeDegree.get(leftNode.slug) || 0);
        if (degreeDelta !== 0) return degreeDelta;
        return leftNode.label.localeCompare(rightNode.label);
      })
      .slice(0, PREVIEW_NODE_LIMIT);
  }, [graphData.edges, graphData.nodes]);
  const previewNodeSlugs = new Set(previewNodes.map((node) => node.slug));
  const previewEdges = graphData.edges.filter(
    (edge) => previewNodeSlugs.has(edge.sourceSlug) && previewNodeSlugs.has(edge.targetSlug),
  );
  const hiddenNodeCount = Math.max(0, graphData.nodes.length - previewNodes.length);
  const canVisualize = graphData.nodes.length > 1 && graphData.edges.length > 0;

  return (
    <>
      <div className="relative overflow-hidden rounded-lg border border-border bg-card">
        <div className="pointer-events-none absolute inset-0 opacity-20 [background-image:linear-gradient(var(--border)_1px,transparent_1px),linear-gradient(90deg,var(--border)_1px,transparent_1px)] [background-size:22px_22px]" />
        <div className="relative h-[300px]">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-[13px] text-muted-foreground">
              Loading relationship graph...
            </div>
          ) : !canVisualize ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center">
              <p className="text-[13px] font-medium text-foreground">No relationship definitions available yet</p>
              <p className="text-[12px] text-muted-foreground">
                Add relationship definitions to power graph visualization and object-search traversal.
              </p>
            </div>
          ) : (
            <>
              <RelationshipGraphCanvas
                nodes={previewNodes}
                edges={previewEdges}
                width={980}
                height={300}
                compact
              />
              {hiddenNodeCount > 0 && (
                <div className="pointer-events-none absolute right-3 top-3 rounded border border-border bg-card px-2 py-1 text-[10px] text-muted-foreground">
                  +{hiddenNodeCount} more objects
                </div>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          className="absolute bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-[5px] border border-border bg-card px-3 py-1.5 text-[12px] font-medium text-foreground shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => setDialogOpen(true)}
          disabled={!canVisualize}
        >
          <Eye className="h-3.5 w-3.5" />
          Visualize
        </button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-6xl p-0">
          <div className="border-b border-border px-6 pb-3 pt-5">
            <DialogTitle className="text-[15px] font-semibold text-foreground">Object Relationships</DialogTitle>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Explore relationship definitions used by object search and cross-object filters.
            </p>
          </div>
          <div className="space-y-3 px-6 pb-6 pt-4">
            <div className="h-[72vh] overflow-auto rounded-md border border-border bg-card">
              <div className="min-w-[980px]">
                <RelationshipGraphCanvas
                  nodes={graphData.nodes}
                  edges={graphData.edges}
                  width={1320}
                  height={860}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-muted-foreground">
              <span>{graphData.nodes.length} objects · {graphData.edges.length} relationships</span>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1.5">
                  <svg width="20" height="2" aria-hidden>
                    <line x1="0" y1="1" x2="20" y2="1" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                  enabled
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <svg width="20" height="2" aria-hidden>
                    <line x1="0" y1="1" x2="20" y2="1" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3" />
                  </svg>
                  many-to-many
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Objects List Page ───────────────────────────────────────────────

const ObjectsPage = () => {
  const { workspaceId: urlWorkspaceId } = useParams();
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const [search, setSearch] = useState("");
  const [showSystemObjects, setShowSystemObjects] = useState(true);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);

  const { data: workspaceObjectsData = [], isLoading } = useWorkspaceObjectsDetailed({
    workspaceId,
    enabled: !!workspaceId,
  }) as { data: WorkspaceObjectRow[]; isLoading: boolean };

  const { data: workspaceRelationshipsData = [], isLoading: relationshipsLoading } = useWorkspaceObjectRelationships({
    workspaceId: workspaceId || "",
    includeDisabled: true,
    includeDisabledObjects: true,
    enabled: !!workspaceId,
  }) as { data: WorkspaceObjectRelationshipSummary[]; isLoading: boolean };

  const { data: aiMemoryData } = useAIMemoryObjects({
    workspaceId,
    enabled: !!workspaceId,
  } as any);

  const updateObjectMutation = useUpdateWorkspaceObject();
  const enableAIMemoryMutation = useEnableAIMemoryObject();

  const aiMemoryBySlug = useMemo(
    () => normalizeAiMemoryMap((aiMemoryData as any)?.data),
    [aiMemoryData],
  );

  const allObjects = useMemo<ObjectsTableRow[]>(() => {
    const workspaceObjects = Array.isArray(workspaceObjectsData) ? workspaceObjectsData : [];
    const existingBySlug = new Set(workspaceObjects.map((object) => object.api_slug));

    const vectorRows: ObjectsTableRow[] = VECTOR_OBJECTS
      .filter((vectorObject) => !existingBySlug.has(vectorObject.slug))
      .map((vectorObject) => ({
        id: `vector-${vectorObject.slug}`,
        api_slug: vectorObject.slug,
        singular_noun: vectorObject.name.toLowerCase().replace(/\s+/g, "_"),
        plural_noun: vectorObject.name,
        table_name: vectorObject.slug,
        description: vectorObject.description,
        enabled: aiMemoryBySlug[vectorObject.slug]?.status === "enabled",
        is_system_object: false,
        display_order: 0,
        field_count: aiMemoryBySlug[vectorObject.slug]?.field_count || 0,
        isVectorObject: true,
        aiMemoryStatus: aiMemoryBySlug[vectorObject.slug],
      }));

    const workspaceRows: ObjectsTableRow[] = workspaceObjects.map((object) => ({
      ...object,
      isVectorObject: false,
      aiMemoryStatus: aiMemoryBySlug[object.api_slug],
    }));

    return [...workspaceRows, ...vectorRows];
  }, [aiMemoryBySlug, workspaceObjectsData]);

  const filteredObjects = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return allObjects.filter((object) => {
      if (!showSystemObjects && object.is_system_object) return false;
      if (!normalizedSearch) return true;
      return (
        object.plural_noun.toLowerCase().includes(normalizedSearch)
        || object.api_slug.toLowerCase().includes(normalizedSearch)
        || String(object.description || "").toLowerCase().includes(normalizedSearch)
      );
    });
  }, [allObjects, search, showSystemObjects]);

  const selectedObject = useMemo(
    () => allObjects.find((obj) => obj.id === selectedObjectId) || null,
    [allObjects, selectedObjectId],
  );

  const handleEnableAIMemory = async (slug: AIMemoryObjectType) => {
    if (!workspaceId) return;
    try {
      await enableAIMemoryMutation.mutateAsync({
        workspace_id: workspaceId,
        object_type: slug,
      });

      toast.success(`${VECTOR_OBJECTS.find((item) => item.slug === slug)?.name || "Object"} enabled for AI memory.`);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ai-memory-objects", workspaceId] }),
        queryClient.invalidateQueries({ queryKey: ["workspace-objects-detailed", workspaceId] }),
      ]);
    } catch {
      toast.error("Could not enable object for AI memory.");
    }
  };

  const handleToggleObjectStatus = async (object: ObjectsTableRow, nextEnabled: boolean) => {
    if (!workspaceId || object.isVectorObject || isProtectedObject(object.api_slug)) return;

    try {
      await updateObjectMutation.mutateAsync({
        workspace_id: workspaceId,
        object_id: object.id,
        data: { enabled: nextEnabled },
      });

      toast.success(`${object.plural_noun} ${nextEnabled ? "enabled" : "disabled"}.`);

      await queryClient.invalidateQueries({ queryKey: ["workspace-objects-detailed", workspaceId] });
    } catch {
      toast.error("Unable to update object. Please try again later.");
    }
  };

  // If an object is selected, show the detail view
  if (selectedObject && workspaceId) {
    return (
      <ObjectDetailView
        object={selectedObject}
        workspaceId={workspaceId}
        onBack={() => setSelectedObjectId(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[15px] font-semibold text-foreground">Objects</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Modify and add objects in your workspace</p>
        </div>
        <Button size="sm" className="h-8 gap-1.5 text-[12px]" disabled>
          <Plus className="h-3.5 w-3.5" />
          New custom object
          <span className="ml-0.5 rounded bg-background/20 px-1 py-0.5 text-[9px] uppercase tracking-wider">Soon</span>
        </Button>
      </div>

      <ObjectRelationshipsGraph
        objects={allObjects}
        relationships={workspaceRelationshipsData}
        isLoading={isLoading || relationshipsLoading}
      />

      <div>
        <h2 className="text-[14px] font-semibold text-foreground">Existing objects</h2>
        <p className="mt-0.5 text-[13px] text-muted-foreground">Manage objects, fields and relationships</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search objects"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-8 pl-8 text-[13px]"
          />
        </div>

        <button
          type="button"
          onClick={() => setShowSystemObjects((prev) => !prev)}
          className={cn(
            "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-[12px] transition-colors",
            showSystemObjects
              ? "border-primary/30 bg-primary/5 text-foreground"
              : "border-border bg-card text-muted-foreground hover:text-foreground",
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {showSystemObjects ? "System objects visible" : "Hide system objects"}
        </button>
      </div>

      <p className="text-[11px] text-muted-foreground">Showing {filteredObjects.length} of {allObjects.length} objects</p>

      {/* Simple grid table */}
      <div className="overflow-hidden rounded-lg border border-border">
        {/* Header */}
        <div className="grid grid-cols-[1fr_100px_100px_100px_auto] items-center bg-muted/50 px-4 py-2.5 text-[11px] font-medium text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3 w-3" />
            Object
          </div>
          <div>Type</div>
          <div>Records</div>
          <div>Attributes</div>
          <div />
        </div>

        {/* Rows */}
        <div className="divide-y divide-border">
          {isLoading ? (
            <div className="px-4 py-12 text-center text-[13px] text-muted-foreground">
              Loading objects...
            </div>
          ) : filteredObjects.length > 0 ? (
            filteredObjects.map((object) => {
              const iconConfig = ICON_STYLE_BY_SLUG[object.api_slug] || defaultIconStyle;
              const Icon = iconConfig.icon;
              const recordCount = object.record_count ?? object.records_count ?? null;
              const attributeCount = object.field_count || object.aiMemoryStatus?.field_count || 0;
              const typeLabel = object.isVectorObject ? "Vector" : object.is_system_object ? "Standard" : "Custom";
              const clickable = !object.isVectorObject && object.enabled;
              const disableToggle = isProtectedObject(object.api_slug) || object.isVectorObject;

              return (
                <div
                  key={object.id}
                  onClick={() => clickable && setSelectedObjectId(object.id)}
                  className={cn(
                    "grid grid-cols-[1fr_100px_100px_100px_auto] items-center px-4 py-3 transition-colors",
                    clickable ? "cursor-pointer hover:bg-muted/50" : "",
                    !object.enabled ? "opacity-70" : "",
                  )}
                >
                  {/* Object name + icon */}
                  <div className="flex items-center gap-3">
                    <div className={cn("flex h-7 w-7 items-center justify-center rounded-md text-white", iconConfig.color)}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className={cn("truncate text-[13px] font-medium", object.enabled ? "text-foreground" : "text-muted-foreground")}>
                        {object.plural_noun}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">{object.api_slug}</p>
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <span className="rounded bg-muted px-2 py-0.5 text-[12px] text-muted-foreground">
                      {typeLabel}
                    </span>
                  </div>

                  {/* Records */}
                  <div>
                    {object.enabled && typeof recordCount === "number" ? (
                      <span className="text-[13px] font-medium text-foreground">{recordCount.toLocaleString()}</span>
                    ) : (
                      <span className="text-[13px] text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* Attributes */}
                  <div>
                    {object.enabled ? (
                      <span className="text-[13px] font-medium text-foreground">{attributeCount}</span>
                    ) : (
                      <span className="text-[13px] text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                    {object.isVectorObject ? (
                      object.aiMemoryStatus?.status === "enabled" ? (
                        <span className="text-[11px] text-muted-foreground">Enabled</span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[11px]"
                          onClick={() => handleEnableAIMemory(object.api_slug as AIMemoryObjectType)}
                          disabled={enableAIMemoryMutation.isLoading}
                        >
                          Enable Search
                        </Button>
                      )
                    ) : object.enabled ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted"
                            aria-label="Object actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => setSelectedObjectId(object.id)}>
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleObjectStatus(object, false).catch(() => {})}
                            disabled={disableToggle}
                            className="text-destructive focus:text-destructive"
                          >
                            Disable object
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        onClick={() => handleToggleObjectStatus(object, true).catch(() => {})}
                        disabled={disableToggle || updateObjectMutation.isLoading}
                      >
                        Activate
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-12 text-center text-[13px] text-muted-foreground">
              No objects found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjectsPage;
